import { useEffect, useState } from 'react';
import { useDistrictDetail } from '../../hooks/useDistricts';
import { motion, AnimatePresence } from 'framer-motion';
import { getRatingColor, formatMargin } from '../../lib/export';
import { LoadingState } from './LoadingStates';
import { trackGrowth } from '../../lib/growth';

export default function DistrictDetail({ district, onClose }) {
  const [shareLabel, setShareLabel] = useState('Share');
  // Defensive check for missing district
  if (!district) {
    onClose();
    return null;
  }

  const { data: fullDistrict, isLoading } = useDistrictDetail(district.district_code);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const displayDistrict = fullDistrict || district;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="bg-slate-900 w-full rounded-t-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag Handle */}
          <div className="sticky top-0 bg-slate-900 z-10 border-b border-slate-700">
            <div className="flex items-center justify-between p-3 sm:p-4">
              <div className="flex-1 text-center">
                <div className="w-10 sm:w-12 h-1 bg-slate-600 rounded-full mx-auto mb-2" />
                <h2 className="text-lg sm:text-xl font-bold">{displayDistrict.district_code || 'Unknown District'}</h2>
                <p className="text-xs sm:text-sm text-slate-400 line-clamp-1">{displayDistrict.name || 'District name not available'}</p>
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-white text-2xl w-11 h-11 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="p-4">
              <LoadingState />
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {/* Rating Summary */}
              <div className="bg-accent-blue-dark/10 border-2 border-accent-blue/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-slate-400">Model Rating</div>
                    <div className="text-2xl font-bold text-white">
                      {displayDistrict.model_rating || 'N/A'}
                    </div>
                    {displayDistrict.model_confidence && (
                      <div className="text-xs text-slate-400">
                        {(displayDistrict.model_confidence * 100).toFixed(0)}% confidence
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-400">2024 Margin</div>
                    <div className="text-2xl font-bold">
                      {formatMargin(displayDistrict.margin_2024)}
                    </div>
                    <div className="text-xs text-slate-400">
                      PVI: {displayDistrict.pvi || 'EVEN'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Candidates */}
              <Section
                title="👤 Candidates"
                count={fullDistrict?.candidates?.length}
              >
                {!fullDistrict?.candidates || fullDistrict.candidates.length === 0 ? (
                  <div className="text-center py-6 sm:py-8 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="text-3xl sm:text-4xl mb-2">🔍</div>
                    <div className="font-semibold text-amber-200">
                      No candidates filed yet
                    </div>
                    <div className="text-xs sm:text-sm text-amber-300/80 mt-1">
                      Recruitment opportunity!
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {fullDistrict.candidates.map(candidate => (
                      <CandidateCard key={candidate.id} candidate={candidate} />
                    ))}
                  </div>
                )}
              </Section>

              {/* Demographics */}
              {fullDistrict?.district_demographics?.[0] && (
                <Section title="📊 Demographics">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <DataPoint
                      label="Population"
                      value={fullDistrict.district_demographics[0].total_population?.toLocaleString()}
                    />
                    <DataPoint
                      label="Median Income"
                      value={fullDistrict.district_demographics[0].median_household_income ?
                        `$${fullDistrict.district_demographics[0].median_household_income.toLocaleString()}` : 'N/A'}
                    />
                    <DataPoint
                      label="College Degree"
                      value={fullDistrict.district_demographics[0].pct_bachelors_or_higher ?
                        `${fullDistrict.district_demographics[0].pct_bachelors_or_higher.toFixed(1)}%` : 'N/A'}
                    />
                    <DataPoint
                      label="Median Age"
                      value={fullDistrict.district_demographics[0].median_age?.toFixed(1)}
                    />
                  </div>
                </Section>
              )}

              {/* Key Dates */}
              {(displayDistrict.filing_deadline || displayDistrict.primary_date || displayDistrict.general_date) && (
                <Section title="📅 Key Dates">
                  <div className="space-y-2 text-sm">
                    {displayDistrict.filing_deadline && (
                      <DateRow
                        label="Filing Deadline"
                        date={displayDistrict.filing_deadline}
                      />
                    )}
                    {displayDistrict.primary_date && (
                      <DateRow
                        label="Primary Election"
                        date={displayDistrict.primary_date}
                      />
                    )}
                    {displayDistrict.general_date && (
                      <DateRow
                        label="General Election"
                        date={displayDistrict.general_date}
                      />
                    )}
                  </div>
                </Section>
              )}

              {/* Actions */}
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-2">
                <button
                  onClick={async () => {
                    const districtCode = displayDistrict.district_code || '';
                    const shareUrl = new URL(window.location.origin);
                    if (districtCode) shareUrl.searchParams.set('district', districtCode);
                    shareUrl.searchParams.set('utm_source', 'player_share');
                    shareUrl.searchParams.set('utm_medium', 'referral');
                    shareUrl.searchParams.set('utm_campaign', '2026_house_map');
                    if (navigator.share) {
                      trackGrowth('share_started', { method: 'native', district: districtCode || 'unknown' });
                      await navigator.share({
                        title: `${displayDistrict.district_code || 'District'} - 2026 House Race`,
                        text: `Check out this race: ${displayDistrict.name || displayDistrict.district_code || 'Unknown District'}`,
                        url: shareUrl.toString()
                      }).catch(() => {
                        // Silently fail if share is cancelled
                      });
                    } else if (navigator.clipboard) {
                      await navigator.clipboard.writeText(shareUrl.toString());
                      trackGrowth('share_started', { method: 'copy_link', district: districtCode || 'unknown' });
                      setShareLabel('Link copied');
                      window.setTimeout(() => setShareLabel('Share'), 1800);
                    }
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-accent-blue-dark text-white rounded-lg font-semibold hover:bg-accent-blue-dark/90 active:scale-95 transition-transform"
                >
                  <span>📤</span> {shareLabel}
                </button>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-600 active:scale-95 transition-transform"
                >
                  <span>✕</span> Close
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Section({ title, count, children }) {
  return (
    <div>
      <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
        {title}
        {count !== undefined && count !== null && count > 0 && (
          <span className="text-sm bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">
            {count}
          </span>
        )}
      </h3>
      {children}
    </div>
  );
}

function CandidateCard({ candidate }) {
  // Defensive check for missing candidate
  if (!candidate) return null;

  // Normalized candidate data - works with all_candidates_2026 schema
  const name = candidate.full_name || candidate.name || 'Unknown Candidate';
  const party = candidate.party || 'Party not specified';
  const isIncumbent = candidate.is_incumbent || false;
  const website = candidate.campaign_website || candidate.website;
  const status = candidate.status;

  // Determine if Democratic or Republican for styling
  const isDem = party?.toLowerCase().includes('democrat');
  const isRep = party?.toLowerCase().includes('republican');

  const getPartyIcon = (party) => {
    if (!party) return null;
    if (party.toLowerCase().includes('democrat')) {
      return '/images/democrat.jpg';
    }
    if (party.toLowerCase().includes('republican')) {
      return '/images/republican.jpg';
    }
    return null;
  };

  return (
    <div className={`
      border-2 rounded-lg p-3 transition-all
      ${isDem
        ? 'border-blue-500/50 bg-blue-900/20'
        : isRep
        ? 'border-red-500/50 bg-red-900/20'
        : 'border-slate-600 bg-slate-800/50'
      }
    `}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {getPartyIcon(party) && (
              <img
                src={getPartyIcon(party)}
                alt={party}
                className="w-8 h-8 rounded object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div>
              <div className="font-semibold text-white">
                {name}
                {isIncumbent && (
                  <span className="ml-2 text-xs bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 px-2 py-0.5 rounded">
                    Incumbent
                  </span>
                )}
              </div>
              <div className={`text-sm ${isDem ? 'text-blue-300' : isRep ? 'text-red-300' : 'text-slate-400'}`}>
                {party}
              </div>
            </div>
          </div>
        </div>
        {status && status !== 'Active' && (
          <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
            {status}
          </span>
        )}
      </div>

      {website && (
        <a
          href={website.startsWith('http') ? website : `https://${website}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block text-sm text-accent-blue hover:underline"
        >
          🔗 Campaign Website →
        </a>
      )}

      {/* Social media links */}
      {(candidate.twitter_handle || candidate.facebook_url) && (
        <div className="mt-2 flex gap-3 text-sm">
          {candidate.twitter_handle && (
            <a
              href={`https://twitter.com/${candidate.twitter_handle.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-blue-400"
            >
              𝕏 @{candidate.twitter_handle.replace('@', '')}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function DataPoint({ label, value }) {
  return (
    <div>
      <div className="text-slate-400">{label || 'Unknown'}</div>
      <div className="font-semibold text-white">
        {value !== undefined && value !== null && value !== '' ? value : 'N/A'}
      </div>
    </div>
  );
}

function DateRow({ label, date }) {
  // Defensive check for missing or invalid date
  if (!date) return null;

  const dateObj = new Date(date);
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return null;

  const daysUntil = Math.ceil((dateObj - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-700 last:border-b-0">
      <div className="font-medium text-slate-300">{label}</div>
      <div className="text-right">
        <div className="font-semibold text-white">
          {dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
        {daysUntil > 0 && daysUntil < 90 && (
          <div className="text-xs text-amber-400">
            {daysUntil} days away
          </div>
        )}
      </div>
    </div>
  );
}
